import { useLocale } from '@iris/i18n/react';
import { useAuth } from '@iris/ui/auth/auth-provider';
import { RouteFallback } from '@iris/ui/components/app-screens';
import { BackButton, parentPath } from '@iris/ui/components/back-button';
import { BrandMark } from '@iris/ui/components/brand-mark';
import { LanguageSwitch } from '@iris/ui/components/language-switch';
import { OfflineBanner } from '@iris/ui/components/offline-banner';
import { PhoneFrame } from '@iris/ui/components/phone-frame';
import { cn } from '@iris/ui/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { Compass, Inbox, QrCode, UserRound, WalletCards } from 'lucide-react';
import { lazy, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation, useSearchParams } from 'react-router-dom';

import { useInbox } from './notify/use-inbox';
import { useQrSeed } from './qr/use-qr';
import { UpdatePrompt } from './update-prompt';

const DeletionPending = lazy(async () => ({
  default: (await import('./privacy/privacy-kit')).DeletionPending,
}));

/** Top-level screens: the tabs and the sign-in flows have no back arrow (ADR 0011 D6). */
const TOP_LEVEL = [
  '/',
  '/explore',
  '/inbox',
  '/profile',
  '/qr',
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/welcome',
];
/** Screens whose parent is not the path above them. */
const PARENTS = [
  [/^\/m\//, '/explore'],
  [/^\/(cards|vouchers|j)\//, '/'],
] as const;

/** Screens without the bottom bar (T4 §1.1): sign-in flows and a voucher shown at the counter. */
const NO_TABS = ['/vouchers/', '/welcome', '/login', '/signup', '/forgot-password', '/reset-password'];

/**
 * App frame: offline banner, brand, language switch and — once signed in — the bottom bar with My QR in the center
 * (FR-QR-01), Explorer and the inbox with its unread count (T4 §1.1: 5 tabs). Signed out, Explore and the shop pages
 * are public (owner decision M21) and the header offers "Se connecter". The brand name comes from one key (brand.name).
 */
export function Layout() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { changeLocale, status, user } = useAuth();
  const { pathname } = useLocation();
  const signedIn = status === 'authenticated' || status === 'offline';
  // ADR 0009 D2: while a deletion is pending, every screen is the deletion screen
  const pending = signedIn && user?.status === 'PENDING_DELETION';
  const tabs = signedIn && !pending && !NO_TABS.some((path) => pathname.startsWith(path));
  const publicPage = !signedIn && (pathname.startsWith('/explore') || pathname.startsWith('/m/'));
  const parent = parentPath(pathname, TOP_LEVEL, PARENTS);

  useEffect(() => {
    document.title = t('brand.name');
  }, [t, locale]);

  return (
    <PhoneFrame>
      <div className="flex min-h-dvh flex-col bg-neutral-50 md:min-h-full">
        <OfflineBanner message={t('common.offline')} />
        {signedIn ? <QrSeedSync /> : null}
        {signedIn ? <OpenedFromPush /> : null}
        <UpdatePrompt />
        <header className="sticky top-0 z-30 bg-neutral-50/80 backdrop-blur-md supports-[backdrop-filter]:bg-neutral-50/70">
          <div className="mx-auto flex w-full max-w-[480px] items-center justify-between gap-3 px-4 py-2.5">
            <div className="flex min-w-0 items-center gap-1">
              {parent ? <BackButton label={t('common.back')} fallback={parent} /> : null}
              <Link
                to="/"
                className="flex items-center gap-2 text-lg font-semibold tracking-tight text-primary"
              >
                <BrandMark className="h-7 w-7" />
                {t('brand.name')}
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitch
                value={locale}
                onChange={changeLocale}
                label={t('common.language.label')}
                names={{ fr: t('common.language.fr'), ar: t('common.language.ar') }}
              />
              {publicPage ? (
                <Link to="/login" className="text-sm font-medium text-primary hover:underline">
                  {t('nav.signIn')}
                </Link>
              ) : null}
            </div>
          </div>
        </header>
        <main className={cn('mx-auto w-full max-w-[480px] flex-1 px-4 py-6', tabs && 'pb-28')}>
          <Suspense fallback={<RouteFallback label={t('common.loading')} />}>
            <div
              key={pathname}
              className="animate-in fade-in-0 slide-in-from-bottom-2 duration-300 motion-reduce:animate-none"
            >
              {pending ? <DeletionPending /> : <Outlet />}
            </div>
          </Suspense>
        </main>
        {tabs ? <TabBar /> : null}
      </div>
    </PhoneFrame>
  );
}

/**
 * A push opens the app with `?n=<message id>`: the message is marked read (FR-PRO-04 "opened" for promotions) and the
 * marker removed from the address.
 */
function OpenedFromPush() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const id = params.get('n');
  useEffect(() => {
    if (!id) return;
    void api
      .POST('/api/v1/inbox/{id}/read', { params: { path: { id } } })
      .then(() => queryClient.invalidateQueries({ queryKey: ['inbox'] }));
    setParams(
      (current) => {
        const copy = new URLSearchParams(current);
        copy.delete('n');
        return copy;
      },
      { replace: true },
    );
  }, [api, id, queryClient, setParams]);
  return null;
}

/** Keeps the QR seed on the device from the first screen, so My QR works later without network (NFR-06). */
function QrSeedSync() {
  useQrSeed();
  return null;
}

function TabBar() {
  const { t } = useTranslation();
  const inbox = useInbox();
  const unread = inbox.data?.pages[0]?.unread ?? 0;
  // ADR 0013: the active tab's icon sits in a soft pill; a press shrinks it a little
  const tab = ({ isActive }: { isActive: boolean }) =>
    cn(
      'group flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] transition-colors duration-200 active:scale-95',
      isActive ? 'font-semibold text-primary' : 'text-muted-foreground',
    );
  const pill =
    'flex h-7 w-12 items-center justify-center rounded-lg transition-colors duration-200 group-aria-[current=page]:bg-primary/10';
  return (
    <nav
      aria-label={t('nav.label')}
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/85 pb-[env(safe-area-inset-bottom)] shadow-[0_-10px_30px_-18px_rgb(0_0_0/0.25)] backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-[480px] items-end">
        <NavLink to="/" end className={tab}>
          <span className={pill}>
            <WalletCards aria-hidden className="h-5 w-5" />
          </span>
          {t('nav.home')}
        </NavLink>
        <NavLink to="/explore" className={tab}>
          <span className={pill}>
            <Compass aria-hidden className="h-5 w-5" />
          </span>
          {t('nav.explore')}
        </NavLink>
        <NavLink
          to="/qr"
          className="-mt-7 flex flex-1 flex-col items-center gap-1 pb-2 text-[11px] font-semibold text-primary transition-transform duration-150 active:scale-95"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-brand-700 text-primary-foreground shadow-lg shadow-primary/35 ring-4 ring-background">
            <QrCode aria-hidden className="h-7 w-7" />
          </span>
          {t('nav.qr')}
        </NavLink>
        <NavLink to="/inbox" className={tab}>
          <span className={cn(pill, 'relative')}>
            <Inbox aria-hidden className="h-5 w-5" />
            {unread > 0 ? (
              <span className="absolute -top-1.5 start-3 min-w-4 rounded-full bg-destructive px-1 text-center text-[10px] font-semibold leading-4 text-destructive-foreground">
                <bdi dir="ltr">{unread > 9 ? '9+' : unread}</bdi>
              </span>
            ) : null}
          </span>
          {t('nav.inbox')}
        </NavLink>
        <NavLink to="/profile" className={tab}>
          <span className={pill}>
            <UserRound aria-hidden className="h-5 w-5" />
          </span>
          {t('nav.profile')}
        </NavLink>
      </div>
    </nav>
  );
}
